// NOTICE: KAPTION-INTEGRATION
// usage-analytics.LIB.1 — Product-usage analytics client (telemetry library).
//
// This is a NEW, separate pipeline from the PostHog AnalyticsHelper. It batches
// METADATA-ONLY product-usage events to the Kaption usage-analytics Cloudflare
// Worker, authenticated with a short-lived JWT minted by the Rails endpoint
// GET /api/v1/accounts/:account_id/usage_analytics/token.
//
// HARD RULES:
//   - Fully inert when the KAPTION_USAGE_ANALYTICS flag is off.
//   - Best-effort: NOTHING here may throw into product code (mirrors useTrack).
//   - NEVER read meta.sender / contact / end-customer name. Identity is
//     account + agent(user) + inbox + conversation_id only. No message text —
//     callers pass length buckets / hashes, never content.
//
// Usage from a component method (Options or Composition API):
//   import { trackUsage } from 'dashboard/composables/useUsageAnalytics';
//   trackUsage(USAGE_ANALYTICS_EVENTS.MESSAGE_SENT, { ...metadata });
//
// Or as a composable in <script setup>:
//   const { track } = useUsageAnalytics();
//
// NOTE: the Vuex store is INJECTED at app bootstrap via setUsageAnalyticsStore
// rather than imported at module scope. Importing 'dashboard/store' here would
// eagerly evaluate the real store (createStore + the whole module graph) the
// moment any component that uses this composable is pulled into a test's import
// graph, breaking specs that partially mock 'vuex' / 'dashboard/store' deps.
import transport from 'dashboard/helper/usageAnalytics/transport';
import {
  SOURCE,
  normalizeChannelType,
} from 'dashboard/helper/usageAnalytics/constants';

// usage-analytics.LIB.0 — the injected Vuex store singleton. Set once from the
// dashboard app bootstrap (where the store is created). Until then it is null
// and every helper below stays inert/best-effort (never throws).
let store = null;

// Called once from the dashboard entrypoint after the store is created.
export function setUsageAnalyticsStore(injectedStore) {
  store = injectedStore;
}

// crypto.randomUUID with a defensive fallback for non-secure contexts / jsdom.
/* eslint-disable no-bitwise -- bitwise ops are intrinsic to the RFC4122 fallback */
export function safeUuid() {
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }
  } catch (e) {
    // fall through
  }
  // RFC4122-ish fallback (not cryptographically strong; only used when
  // crypto.randomUUID is unavailable).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
/* eslint-enable no-bitwise */

// usage-analytics.IDENTITY.2 — one session id per tab/session, generated lazily.
let SESSION_ID = null;
function getSessionId() {
  try {
    if (!SESSION_ID) {
      SESSION_ID = safeUuid();
    }
    return SESSION_ID;
  } catch (e) {
    return 'unknown';
  }
}

let CONFIGURED = false;

// usage-analytics.LIB.2 — flag gate. Reads the Kaption FF store getter directly
// (same getter useKaptionFeatureFlags uses). Returns false on any error so the
// library is inert by default.
export function isUsageAnalyticsEnabled() {
  try {
    if (!store) return false;
    const user = store.getters.getCurrentUser;
    const accountId = store.getters.getCurrentAccountId;
    const userId = user && user.id;
    if (!accountId || !userId) return false;
    return Boolean(
      store.getters['kaptionFeatureFlags/isEnabled'](
        'KAPTION_USAGE_ANALYTICS',
        accountId,
        userId
      )
    );
  } catch (e) {
    return false;
  }
}

// Configure the transport's token provider + 401 handler exactly once. The
// provider asks the store to mint/refresh the analytics JWT (best-effort).
function ensureConfigured() {
  if (CONFIGURED) return;
  if (!store) return;
  CONFIGURED = true;
  try {
    transport.configure({
      tokenProvider: async () => {
        try {
          // fetchToken returns cached creds unless expiring soon; never throws.
          return await store.dispatch('usageAnalytics/fetchToken');
        } catch (e) {
          return null;
        }
      },
      onUnauthorized: () => {
        try {
          store.dispatch('usageAnalytics/invalidateToken');
        } catch (e) {
          // swallow
        }
      },
    });
  } catch (e) {
    // swallow
  }
}

// usage-analytics.IDENTITY.3 — assemble the identity block from ROOT Vuex
// getters. inbox_id comes from the selected chat; inbox_channel_type is the
// inbox's channel_type normalized to a short enum. NEVER touches contact / sender.
function buildIdentity() {
  const identity = {
    account_id: null,
    account_name: null,
    user_id: null,
    user_name: null,
    user_email: null,
    inbox_id: null,
    inbox_channel_type: null,
    conversation_id: null,
    display_id: null,
  };
  try {
    const user = store.getters.getCurrentUser || {};
    const account = store.getters.getCurrentAccount || {};
    identity.account_id =
      account.id ?? store.getters.getCurrentAccountId ?? null;
    identity.account_name = account.name ?? null;
    identity.user_id = user.id ?? null;
    identity.user_name = user.name ?? null;
    identity.user_email = user.email ?? null;

    const chat = store.getters.getSelectedChat || {};
    identity.conversation_id = chat.id ?? null;
    identity.display_id = chat.display_id ?? null;
    const inboxId = chat.inbox_id ?? null;
    identity.inbox_id = inboxId;
    if (inboxId != null) {
      const inbox = store.getters['inboxes/getInbox'](inboxId) || {};
      identity.inbox_channel_type = normalizeChannelType(inbox.channel_type);
    }
  } catch (e) {
    // partial identity is fine; never throw
  }
  return identity;
}

// usage-analytics.PRIVACY.2 — defensive metadata sanitizer. Drops null/undefined
// and guards against accidentally large strings (callers should already pass
// only enums / buckets / hashes / counts, but this is a backstop so no free
// text or PII can slip through a typo).
function sanitizeMetadata(metadata) {
  const clean = {};
  try {
    Object.keys(metadata || {}).forEach(key => {
      const value = metadata[key];
      if (value === null || value === undefined) return;
      if (typeof value === 'string' && value.length > 256) {
        // Never emit a long string as a metadata value.
        return;
      }
      clean[key] = value;
    });
  } catch (e) {
    return {};
  }
  return clean;
}

// usage-analytics.ENVELOPE.1 — build the full event envelope. Every event gets
// this base; event-specific metadata is merged on top.
export function buildEnvelope(eventName, metadata = {}) {
  let version = null;
  try {
    version = store.getters['globalConfig/get']?.appVersion ?? null;
  } catch (e) {
    version = null;
  }
  return {
    event_id: safeUuid(),
    event_name: eventName,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    version,
    source: SOURCE,
    ...buildIdentity(),
    ...sanitizeMetadata(metadata),
  };
}

// usage-analytics.LIB.3 — the public emit. Inert when the flag is off; never
// throws. This is the singleton entry usable from any component method.
export function trackUsage(eventName, metadata = {}) {
  try {
    if (!eventName) return;
    if (!isUsageAnalyticsEnabled()) return;
    ensureConfigured();
    // Warm the token cache (async, best-effort) so the first flush has creds.
    try {
      store.dispatch('usageAnalytics/fetchToken');
    } catch (e) {
      // swallow
    }
    transport.enqueue(buildEnvelope(eventName, metadata));
  } catch (e) {
    // swallow — telemetry is non-critical
  }
}

// usage-analytics.LIB.4 — flush trigger for route changes (and any explicit
// flush point). Best-effort.
export function flushUsage() {
  try {
    transport.flush();
  } catch (e) {
    // swallow
  }
}

// Composable form. Returns the same best-effort helpers.
export function useUsageAnalytics() {
  return {
    track: trackUsage,
    flush: flushUsage,
    isEnabled: isUsageAnalyticsEnabled,
  };
}

export default useUsageAnalytics;
